/**
 * Lightweight audio manager for UI sounds.
 * Preloads hover + click WAVs and plays them on demand.
 * Uses a small pool of Audio elements per sound to handle rapid-fire triggers
 * without cutting off the previous playback.
 */

const POOL_SIZE = 4;

function createPool(src: string): HTMLAudioElement[] {
  const pool: HTMLAudioElement[] = [];
  for (let i = 0; i < POOL_SIZE; i++) {
    const a = new Audio(src);
    a.volume = 0.3;
    a.preload = 'auto';
    pool.push(a);
  }
  return pool;
}

// Eagerly preload all pools on import so the first play has zero latency
const hoverPool = createPool('./audio/hover.wav');
const clickPool = createPool('./audio/click.wav');
const endTurnPool = createPool('./audio/end-turn.wav');
const pistolKillPool = createPool('./audio/Pistol Kill.wav');
const knifeKillPool = createPool('./audio/Knife Kill.wav');
const unarmedKillPool = createPool('./audio/Unarmed Kill.wav');
const sniperKillPool = createPool('./audio/Sniper Kill.wav');
const warningPool = createPool('./audio/warning.wav');
let hoverIdx = 0;
let clickIdx = 0;
let endTurnIdx = 0;
let pistolKillIdx = 0;
let knifeKillIdx = 0;
let unarmedKillIdx = 0;
let sniperKillIdx = 0;
let warningIdx = 0;

export function playHover() {
  const a = hoverPool[hoverIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.15;
  a.play().catch(() => {});
  hoverIdx++;
}

export function playClick() {
  const a = clickPool[clickIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.3;
  a.play().catch(() => {});
  clickIdx++;
}

export function playEndTurn() {
  const a = endTurnPool[endTurnIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.35;
  a.play().catch(() => {});
  endTurnIdx++;
}

export function playPistolKill() {
  const a = pistolKillPool[pistolKillIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.5;
  a.play().catch(() => {});
  pistolKillIdx++;
}

export function playKnifeKill() {
  const a = knifeKillPool[knifeKillIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.5;
  a.play().catch(() => {});
  knifeKillIdx++;
}

export function playUnarmedKill() {
  const a = unarmedKillPool[unarmedKillIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.5;
  a.play().catch(() => {});
  unarmedKillIdx++;
}

export function playSniperKill() {
  const a = sniperKillPool[sniperKillIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.5;
  a.play().catch(() => {});
  sniperKillIdx++;
}

export function playWarning() {
  const a = warningPool[warningIdx % POOL_SIZE];
  a.currentTime = 0;
  a.volume = 0.5;
  a.play().catch(() => {});
  warningIdx++;
}

/**
 * Play the appropriate kill sound based on the weapon used. If no weapon,
 * plays the unarmed kill sound (improvised strike / bare hands).
 */
export function playKillSound(weaponId?: string) {
  if (!weaponId) { playUnarmedKill(); return; }
  if (weaponId.startsWith('pistol')) { playPistolKill(); return; }
  if (weaponId.startsWith('sniper')) { playSniperKill(); return; }
  if (weaponId.startsWith('combat-knife')) { playKnifeKill(); return; }
  // Fallback for any unknown weapon
  playPistolKill();
}
